import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EditIcon from '@material-ui/icons/Edit';
import {
  Grid,
  Card,
  Button,
  Container,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import {
  EmptyState,
  IdeaGeneral,
  IdeaResults,
  BackNavAdmin,
  HistoryLayout,
  IdeaExecution,
  IdeaConclusion,
  IdeaEngagement,
  SidebarIdeaDetails,
  QualificationLayout,
  IdeaGeneralEvaluationLayout,
  SkeletonCardIdeaDetails,
  SkeletonSidebarIdeaDetails,
  DialogAllCreateIdea,
} from '../../components';
import {
  ideaActions,
  portfolioExecutionFlow,
  personalizedClassificationActions,
  multiAreaCascadeActions,
  ideaCreateActions,
  fluxOfIdeasActions,
  ideaCreateDialogsActions
} from '../../actions';
import emptyStateIcon from '../../assets/imgs/emptystate/illustration_empty-state-general-evaluation.svg';
import { useCanEditableIdea } from '../../hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingLeft: 14,
      paddingRight: 14,
    },
  },
  card: {
    border: `1px solid ${theme.palette.neutrals.high.medium}`,
    borderRadius: 8,
    padding: theme.spacing(4),
  }
}));

export const IdeaDetailsPage = () => {
  const { id, page } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const ideaCreate = useSelector((state) => state.ideaCreate);
  const { imagineLocals } = useSelector((state) => state.domains);
  const { fields } = useSelector(state => state.multiAreaCascade);
  const { ideaDetailsFlux, loadingFlux } = useSelector((state) => state.fluxIdeas);
  const { typeProfile, _id: userId } = useSelector((state) => state.user);

  const buls = ideaDetailsFlux?.businessUnitLocal?.map(item => item)[0];
  const isDraft = ideaDetailsFlux?.statusIdea?.code === 'DRAFT';

  const menuOptions = [
    'general',
    'qualification',
    'general-evaluation',
    'execution',
    'conclusion',
    'results',
    'engagement',
    'history'
  ];

  useEffect(() => {
    if (menuOptions.includes(page)) {
      if (!ideaDetailsFlux?._id || ideaDetailsFlux?._id !== id) {
        dispatch(fluxOfIdeasActions.getIdeaInFlux(id));
      }
    } else {
      history.goBack();
    }
  }, [page, id, ideaDetailsFlux, dispatch]);

  useEffect(() => {
    dispatch(ideaActions.clearApprovedDetails());
    dispatch(portfolioExecutionFlow.getProjects(id));
    dispatch(personalizedClassificationActions.getPersonalizedClassification());
  }, [dispatch, id]);

  useEffect(() => {
    if (isDraft) {
      dispatch(ideaCreateActions.loadIdeaDetailsDraft(ideaDetailsFlux));
      dispatch(multiAreaCascadeActions.loadFields(ideaDetailsFlux?.businessUnitLocal, imagineLocals));
    }
  }, [ideaDetailsFlux]);

  useEffect(() => {
    if (fields.map(field => field.selectedBusinessUnit).length > 0) {
      const multiAreaFields = fields.map(item => item.selectedBusinessUnit);
      dispatch(ideaCreateActions.setSelectedBusiness(multiAreaFields));
    }
  }, [fields]);

  useEffect(() => {
    return () => dispatch(ideaActions.clearIdeaDetails());
  }, [dispatch]);

  const canEditIdea = () => {
    const isContributor = ideaDetailsFlux?.contributors?.some(item => item?._id === userId);
    const isOwner = ideaDetailsFlux?.user?._id === userId;
    const isAdmin = typeProfile.type === 'admin';
    const stepId = ideaDetailsFlux?.currentStepFlux?.ideaStepFlux?._id;

    const stepHasEditableIdea = useCanEditableIdea(stepId);
    const rolesVerify = (isContributor || isOwner || isAdmin);

    return rolesVerify && stepHasEditableIdea;
  };

  const shouldRenderEditButton = canEditIdea();

  function handleEditIdea() {
    if (isDraft) {
      dispatch(ideaCreateDialogsActions.openDialogSendIdea());
    } else {
      history.push(`/idea-edit/${ideaDetailsFlux._id}`);
    }
  }

  const unMaskCurrency = (value) => {
    return value?.replace(/[^\d,]/g, "").replace(",", ".");
  }

  async function handleSendIdea() {
    if (ideaCreate?.challenge?.notChallenge) {
      delete ideaCreate.challenge;
    }

    await dispatch(fluxOfIdeasActions.createIdeaInFlux({
      ...ideaCreate,
      estimatedFinancialReturn: unMaskCurrency(ideaCreate?.estimatedFinancialReturn),
      estimatedExecutionCost: unMaskCurrency(ideaCreate?.estimatedExecutionCost)
    }, t('sua_ideia_foi_enviada')));

    dispatch(fluxOfIdeasActions.getIdeaInFlux(id));
  }

  return (
    <>
      <BackNavAdmin title={loadingFlux ? 'carregando...' : ideaDetailsFlux?.name}>
        {(typeProfile.type === 'admin' || 
          (ideaDetailsFlux?.user?._id === userId && isDraft) || 
          shouldRenderEditButton
        ) && (
          <Button
            color="primary"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditIdea}
            disabled={loadingFlux}
          >
            {isDraft ? t('editar_rascunho') : t('editar_ideia')}
          </Button>
        )}
        {isDraft && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleSendIdea}
            disabled={loadingFlux || !buls || buls === undefined}
            style={{ marginLeft: 10 }}
          >
            {t('enviar')}
          </Button>
        )}
      </BackNavAdmin>
      <Container maxWidth="lg" className={classes.root}>
        <DialogAllCreateIdea />

        <Grid container spacing={4}>
          <Grid item xs={12} sm={12} md={3}>
            {loadingFlux ? <SkeletonSidebarIdeaDetails /> : <SidebarIdeaDetails />}
          </Grid>
          <Grid item xs={12} sm={12} md={9}>
            {loadingFlux 
              ? <SkeletonCardIdeaDetails /> 
              : page && (
                <>
                  {page === "general" && ideaDetailsFlux?._id && <IdeaGeneral />}

                  {page === "qualification" && ideaDetailsFlux?._id && <QualificationLayout />}
                
                  {/* 
                  {loadingFlux ? <CircularProgress /> : (
                    page === "execution" && ideaDetailsFlux._id && !!projects?.length ? 
                    (<LinkedProjects projects={projects} />) : (
                    <IdeaExecution />
                  ))} */}

                  {page === "general-evaluation" && ideaDetailsFlux?._id && (<>
                    {ideaDetailsFlux?.personalizedEvaluationGroup !== undefined 
                      ? <IdeaGeneralEvaluationLayout />
                      : (
                        <Card elevation={0} className={classes.card}>
                          <EmptyState
                            icon={emptyStateIcon}
                            title="Ainda não há avaliações por aqui!"
                            description="A avaliação geral será exibida aqui quando liberada."
                          />
                        </Card>
                      )
                    }
                  </>)}

                  {loadingFlux 
                    ? <CircularProgress /> 
                    : page === "execution" && ideaDetailsFlux?._id && <IdeaExecution />
                  }

                  {page === "conclusion" && ideaDetailsFlux?._id && <IdeaConclusion />}

                  {page === "results" && ideaDetailsFlux?._id && <IdeaResults />}

                  {page === "engagement" && ideaDetailsFlux?._id && <IdeaEngagement />}

                  {page === "history" && ideaDetailsFlux?._id && <HistoryLayout />}
                </>
              )
            }
          </Grid>
        </Grid> 
      </Container>
    </>
  );
}
